import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, type Persona } from '../lib/auth';
import { hasAnyRole, M11_ROLES, M12_ROLES, M14_ROLES } from '../lib/roles';
import { ErrorNote, Spinner } from '../ui/components';

const LOGIN_ERRORS: Record<string, string> = {
  no_access: 'You signed in with One Citizen, but something went wrong granting you access. Try again or contact support.',
  access_denied: 'One Citizen sign-in was cancelled.',
  idp_unreachable: 'One Citizen is not reachable right now — try again shortly.',
  session_expired: 'The sign-in attempt expired — please try again.',
  state_mismatch: 'The sign-in attempt could not be validated — please try again.',
  token_exchange_failed: 'One Citizen rejected the sign-in — please try again.',
  signin_failed: 'Sign-in failed — please try again.',
};

type Group = 'vendor' | 'm11' | 'm12' | 'm14';

const GROUP_META: Record<Group, { label: string; blurb: string }> = {
  vendor: {
    label: 'Traders & investors',
    blurb: 'Apply for trade documents, register as a procurement supplier, submit bids, track your own cases.',
  },
  m11: {
    label: 'Public Procurement (e-GP) staff',
    blurb: 'Draft & publish tenders, run the sealed-bid evaluation committee, confirm awards, sign contracts.',
  },
  m12: {
    label: 'Trade & Investment staff',
    blurb: 'Decide competent-authority referrals, facilitate the one-stop investor journey.',
  },
  m14: {
    label: 'Licensing (SLA) staff',
    blurb: 'Resolve licence requirements, decide board agendas, run enforcement cases.',
  },
};

function groupOf(p: Persona): Group {
  if (p.kind === 'vendor') return 'vendor';
  if (hasAnyRole(p.roles, ...Object.values(M11_ROLES))) return 'm11';
  if (hasAnyRole(p.roles, ...Object.values(M12_ROLES))) return 'm12';
  return 'm14';
}

export function Login() {
  const { personas, config, login, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loginError = params.get('login_error');
  const loginErrorText = loginError ? (LOGIN_ERRORS[loginError] ?? LOGIN_ERRORS.signin_failed) : null;

  const enter = async (p: Persona) => {
    setBusy(p.id);
    setError(null);
    try {
      await login(p.id);
      navigate('/');
    } catch {
      setError('Sign-in failed — is the gateway running?');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-ink-950">
      <div className="mx-auto max-w-5xl px-6 py-12">
        <header className="mb-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-600 text-2xl font-black text-white">
            TI
          </div>
          <h1 className="text-2xl font-black tracking-tight text-white">Trade, Investment &amp; Industry Portal</h1>
          <p className="mt-1 text-sm text-slate-400">
            Republic of Seychelles — public procurement, trade documents, investment facilitation &amp; business licensing
          </p>
          <p className="mt-3 text-xs text-slate-500">
            Just here to browse open tenders, the licence register, or trade reference tables? No sign-in needed —{' '}
            <button className="font-semibold text-brand-100 underline" onClick={() => navigate('/procurement/tenders')}>
              go straight there
            </button>
            .
          </p>
        </header>

        {loginErrorText ? (
          <div className="mx-auto mb-6 max-w-xl">
            <ErrorNote message={loginErrorText} />
          </div>
        ) : null}
        {error ? (
          <div className="mx-auto mb-6 max-w-xl">
            <ErrorNote message={error} />
          </div>
        ) : null}

        {config.oneCitizen ? (
          <div className="mx-auto mb-10 max-w-md text-center">
            <a
              href="/auth/onecitizen/start"
              className="flex w-full items-center justify-center gap-3 rounded-xl bg-brand-600 px-5 py-3.5 text-base font-bold text-white shadow-lg shadow-brand-600/25 transition hover:bg-brand-700"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-xs font-black">1</span>
              Sign in with One Citizen
            </a>
            <p className="mt-2 text-xs text-slate-500">
              Your national digital identity. Staff roles are granted by your office administrator; everyone else gets a
              trader/investor account automatically.
            </p>
          </div>
        ) : null}

        {loading ? <Spinner label="Loading sign-in options…" /> : null}

        {config.devLogin && personas.length > 0 ? (
          <p className="mb-4 text-center text-xs font-semibold uppercase tracking-wider text-amber-400/90">
            Development personas — disabled in production (ONEGOV_DEV_LOGIN=0)
          </p>
        ) : null}

        {(['vendor', 'm11', 'm12', 'm14'] as Group[]).filter(() => config.devLogin).map((group) => {
          const items = personas.filter((p) => groupOf(p) === group);
          if (items.length === 0) return null;
          return (
            <section key={group} className="mb-8">
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-300">{GROUP_META[group].label}</h2>
              <p className="mb-3 mt-0.5 text-xs text-slate-500">{GROUP_META[group].blurb}</p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => enter(p)}
                    disabled={busy !== null}
                    className="rounded-xl border border-ink-700 bg-ink-900 p-4 text-left transition hover:border-brand-500 hover:bg-ink-800 disabled:opacity-60"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-white">{p.name}</span>
                      {busy === p.id ? (
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-500 border-t-white" />
                      ) : null}
                    </div>
                    <div className="mt-0.5 text-xs font-medium text-brand-100">{p.title}</div>
                    <div className="mt-1 text-xs text-slate-400">{p.org}</div>
                    {p.roles.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {p.roles.map((r) => (
                          <span key={r} className="rounded bg-ink-800 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 ring-1 ring-ink-700">
                            {r.replaceAll('_', ' ')}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="rounded bg-ink-800 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 ring-1 ring-ink-700">
                          {p.actingForBusinessId ? `ACTING FOR ${p.actingForBusinessId}` : 'INDIVIDUAL (PERSON)'}
                        </span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
